/**
 * POS Terminal Page
 * =================
 * 
 * Main point of sale checkout interface for processing transactions.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
  Banknote,
  Smartphone,
  User,
  UserCircle,
  Tag,
  Receipt,
  X,
  Check,
  Package,
  PoundSterling,
  ArrowLeft,
  LayoutGrid,
  Settings,
  BarChart3,
  Users,
  Star,
  Sparkles,
  Radio,
  Percent,
  CalendarDays,
  Clock,
  MessageSquare,
} from "lucide-react";

const POSTerminalPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Products and categories
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  
  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Loyalty state
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [redeemingPoints, setRedeemingPoints] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cashTendered, setCashTendered] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Receipt modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedTransaction, setCompletedTransaction] = useState(null);
  
  // Discount modal
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  
  // Product customization modal (for variants & modifiers)
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  
  // Booking modal (POS Integration)
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [linkedBooking, setLinkedBooking] = useState(null);
  
  // Fetch products
  const fetchProducts = async () => {
    try {
      let url = `${API}/pos/products?limit=100&is_active=true`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (selectedCategory && selectedCategory !== "all") url += `&category_id=${selectedCategory}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch pending bookings for POS completion
  const fetchPendingBookings = async () => {
    setLoadingBookings(true);
    try {
      const res = await fetch(`${API}/bookings/pos/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingBookings(data);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoadingBookings(false);
    }
  };
  
  // Link a booking to the cart
  const linkBooking = (booking) => {
    // Add booking balance as a cart item
    setCart([{
      item_id: `booking_${booking.booking_id}`,
      product_id: booking.service_id,
      name: `${booking.service_name} - Balance (${booking.booking_number})`,
      quantity: 1,
      unit_price: booking.final_amount,
      discount_amount: 0,
      is_booking_balance: true,
      booking_id: booking.booking_id
    }]);
    setLinkedBooking(booking);
    setShowBookingModal(false);
    toast.success(`Linked booking ${booking.booking_number}`);
  };
  
  // Clear linked booking
  const clearLinkedBooking = () => {
    setLinkedBooking(null);
    setCart([]);
  };
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/pos/categories?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  
  useEffect(() => {
    fetchCategories();
    fetchLoyaltyConfig();
  }, []);
  
  // Fetch loyalty configuration
  const fetchLoyaltyConfig = async () => {
    try {
      const res = await fetch(`${API}/pos/customers/loyalty-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLoyaltyConfig(data);
      }
    } catch (err) {
      console.error("Error fetching loyalty config:", err);
    }
  };
  
  // Search customers
  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const res = await fetch(`${API}/pos/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerResults(data);
      }
    } catch (err) {
      console.error("Error searching customers:", err);
    } finally {
      setSearchingCustomers(false);
    }
  };
  
  // Handle customer search input
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [customerSearch]);
  
  // Select a customer
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch("");
    setCustomerResults([]);
    setShowCustomerSearch(false);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
    toast.success(`Selected ${customer.name}`);
  };
  
  // Clear selected customer
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
  };
  
  // Handle loyalty points redemption
  const handleRedeemPoints = (points) => {
    if (!loyaltyConfig || !selectedCustomer) return;
    
    // Calculate subtotal first
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    
    const maxRedeemable = Math.min(
      selectedCustomer.loyalty_points,
      Math.floor(subtotal / loyaltyConfig.redemption_value) * loyaltyConfig.redemption_rate
    );
    
    const actualPoints = Math.min(points, maxRedeemable);
    const redemptionUnits = Math.floor(actualPoints / loyaltyConfig.redemption_rate);
    const discount = redemptionUnits * loyaltyConfig.redemption_value;
    
    setPointsToRedeem(redemptionUnits * loyaltyConfig.redemption_rate);
    setLoyaltyDiscount(discount);
  };
  
  useEffect(() => {
    fetchProducts();
  }, [searchQuery, selectedCategory]);
  
  // Calculate totals
  const calculateTotals = useCallback(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const itemDiscounts = cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
    
    let orderDiscount = 0;
    if (currentTransaction?.discount_type) {
      if (currentTransaction.discount_type === "percentage") {
        orderDiscount = Math.round(subtotal * currentTransaction.discount_value / 100);
      } else {
        orderDiscount = Math.round(currentTransaction.discount_value * 100);
      }
    }
    
    const totalDiscount = itemDiscounts + orderDiscount + loyaltyDiscount;
    const taxRate = 20; // 20% VAT
    const taxableAmount = subtotal - totalDiscount;
    const taxAmount = Math.round(taxableAmount * taxRate / (100 + taxRate)); // Extract VAT from inclusive price
    const total = subtotal - totalDiscount;
    
    return {
      subtotal,
      discount: itemDiscounts + orderDiscount,
      loyaltyDiscount,
      tax: taxAmount,
      total,
      itemsCount: cart.length,
      unitsCount: cart.reduce((sum, item) => sum + item.quantity, 0)
    };
  }, [cart, currentTransaction, loyaltyDiscount]);
  
  const totals = calculateTotals();
  
  // Calculate loyalty points to earn for this transaction (after totals)
  const pointsToEarn = loyaltyConfig && totals.total > 0
    ? Math.floor((totals.total / 100) * loyaltyConfig.points_per_pound)
    : 0;
  
  // Add to cart
  const addToCart = async (product) => {
    // Check if product has variants or modifiers that need customization
    const hasVariants = product.has_variants && product.variants?.length > 0;
    const hasModifiers = product.has_modifiers && product.modifiers?.length > 0;
    
    if (hasVariants || hasModifiers) {
      // Open customization modal
      setCustomizingProduct(product);
      // Set default variant if exists
      if (hasVariants) {
        const defaultVariant = product.variants.find(v => v.is_default) || product.variants[0];
        setSelectedVariant(defaultVariant);
      } else {
        setSelectedVariant(null);
      }
      setSelectedModifiers({});
      setShowCustomizeModal(true);
      return;
    }
    
    // Simple product - add directly
    addSimpleProductToCart(product);
  };
  
  // Add simple product (no variants/modifiers) to cart
  const addSimpleProductToCart = (product, variant = null, modifiers = []) => {
    // Generate unique item key based on product, variant, and modifiers
    const modifierKey = modifiers.map(m => `${m.modifier_id}_${m.quantity}`).sort().join('|');
    const itemKey = `${product.product_id}_${variant?.variant_id || 'base'}_${modifierKey}`;
    
    // Check if exact same configuration exists
    const existingIndex = cart.findIndex(item => item.item_key === itemKey);
    
    // Calculate base price (variant price or product retail price)
    const basePrice = variant?.price || variant?.retail_price || product.retail_price;
    
    // Calculate modifier total
    const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price * m.quantity), 0);
    
    // Total unit price
    const unitPrice = basePrice + modifierTotal;
    
    // Build display name
    let displayName = product.name;
    if (variant) {
      displayName += ` - ${variant.name}`;
    }
    if (modifiers.length > 0) {
      const modifierNames = modifiers.map(m => m.quantity > 1 ? `${m.name} x${m.quantity}` : m.name);
      displayName += ` | ${modifierNames.join(', ')}`;
    }
    
    if (existingIndex >= 0) {
      // Update quantity
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      // Add new item
      setCart([...cart, {
        item_id: `local_${Date.now()}`,
        item_key: itemKey,
        product_id: product.product_id,
        variant_id: variant?.variant_id || null,
        variant_name: variant?.name || null,
        modifiers: modifiers,
        sku: variant?.sku || product.sku,
        name: displayName,
        base_name: product.name,
        quantity: 1,
        unit_price: unitPrice,
        base_price: basePrice,
        modifier_total: modifierTotal,
        discount_amount: 0,
        cost_price: variant?.cost_price || product.cost_price
      }]);
    }
    
    toast.success(`Added ${displayName} to cart`);
  };
  
  // Confirm customization and add to cart
  const confirmCustomization = () => {
    if (!customizingProduct) return;
    
    // Check if variant is required
    if (customizingProduct.has_variants && customizingProduct.variants?.length > 0 && !selectedVariant) {
      toast.error(`Please select a ${customizingProduct.variant_label || 'variant'}`);
      return;
    }
    
    // Convert selected modifiers to array
    const modifiersArray = Object.entries(selectedModifiers)
      .filter(([_, qty]) => qty > 0)
      .map(([modifierId, quantity]) => {
        const modifier = customizingProduct.modifiers.find(m => m.modifier_id === modifierId);
        return {
          modifier_id: modifierId,
          name: modifier.name,
          price: modifier.price,
          quantity
        };
      });
    
    addSimpleProductToCart(customizingProduct, selectedVariant, modifiersArray);
    
    // Reset and close modal
    setShowCustomizeModal(false);
    setCustomizingProduct(null);
    setSelectedVariant(null);
    setSelectedModifiers({});
  };
  
  // Update modifier quantity
  const updateModifierQuantity = (modifierId, delta) => {
    const modifier = customizingProduct?.modifiers.find(m => m.modifier_id === modifierId);
    if (!modifier) return;
    
    const currentQty = selectedModifiers[modifierId] || 0;
    const newQty = Math.max(0, Math.min(currentQty + delta, modifier.max_quantity || 99));
    
    setSelectedModifiers(prev => ({
      ...prev,
      [modifierId]: newQty
    }));
  };
  
  // Update cart item quantity
  const updateQuantity = (itemId, delta) => {
    const newCart = cart.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean);
    
    setCart(newCart);
  };
  
  // Remove from cart
  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.item_id !== itemId));
  };
  
  // Clear cart
  const clearCart = () => {
    setCart([]);
    setCurrentTransaction(null);
    setSelectedCustomer(null);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
  };
  
  // Apply discount
  const applyDiscount = async () => {
    if (!discountValue) {
      toast.error("Please enter a discount value");
      return;
    }
    
    setCurrentTransaction(prev => ({
      ...prev,
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      discount_code: discountCode
    }));
    
    setShowDiscountModal(false);
    toast.success("Discount applied");
  };
  
  // Create transaction on server
  const createServerTransaction = async () => {
    // Create transaction with customer if selected
    const txnRes = await fetch(`${API}/pos/transactions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        customer_id: selectedCustomer?.customer_id || null,
        customer_name: selectedCustomer?.name || null
      })
    });
    
    if (!txnRes.ok) {
      throw new Error("Failed to create transaction");
    }
    
    const txn = await txnRes.json();
    
    // Add items to transaction
    for (const item of cart) {
      await fetch(`${API}/pos/transactions/${txn.transaction_id}/items`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_amount: item.discount_amount || 0
        })
      });
    }
    
    // Apply order-level discount if any
    if (currentTransaction?.discount_type) {
      await fetch(`${API}/pos/transactions/${txn.transaction_id}/discount`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          discount_type: currentTransaction.discount_type,
          discount_value: currentTransaction.discount_value,
          discount_code: currentTransaction.discount_code
        })
      });
    }
    
    // Apply loyalty points redemption if any
    if (selectedCustomer && pointsToRedeem > 0) {
      await fetch(`${API}/pos/customers/${selectedCustomer.customer_id}/redeem-points`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          points: pointsToRedeem,
          transaction_id: txn.transaction_id
        })
      });
    }
    
    return txn;
  };

  // Process payment
  const processPayment = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      // Create transaction on server
      const txn = await createServerTransaction();
      
      if (paymentMethod === "card") {
        // For card payments, create Stripe checkout session
        const checkoutRes = await fetch(`${API}/pos/transactions/${txn.transaction_id}/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            origin_url: window.location.origin
          })
        });
        
        if (!checkoutRes.ok) {
          const err = await checkoutRes.json();
          throw new Error(err.detail || "Failed to create checkout session");
        }
        
        const checkoutData = await checkoutRes.json();
        
        // Redirect to Stripe checkout
        toast.info("Redirecting to Stripe...");
        window.location.href = checkoutData.checkout_url;
        return; // Don't clear cart - will be cleared after successful payment
      }
      
      // For cash/softpos payments, process directly
      const payRes = await fetch(`${API}/pos/transactions/${txn.transaction_id}/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          method: paymentMethod,
          amount: paymentMethod === "cash" ? Math.round(parseFloat(cashTendered) * 100) : totals.total
        })
      });
      
      if (!payRes.ok) {
        const err = await payRes.json();
        throw new Error(err.detail || "Payment failed");
      }
      
      const result = await payRes.json();
      
      // If this was a booking balance payment, mark booking as completed
      if (linkedBooking) {
        await fetch(`${API}/bookings/pos/complete/${linkedBooking.booking_id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            transaction_id: result.transaction?.transaction_id,
            payment_method: paymentMethod
          })
        });
      }
      
      // Show receipt
      setCompletedTransaction({
        ...result.transaction,
        receipt_number: result.receipt_number,
        change_due: result.change_due,
        linked_booking: linkedBooking
      });
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      
      // Clear cart and linked booking
      clearCart();
      setLinkedBooking(null);
      
      toast.success("Payment successful!");
      
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.message || "Payment failed");
    } finally {
      setProcessingPayment(false);
    }
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const changeDue = paymentMethod === "cash" && cashTendered 
    ? Math.max(0, parseFloat(cashTendered) * 100 - totals.total)
    : 0;
  
  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 text-gray-900 dark:text-white">
      {/* Top Bar - Brand Gradient Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 dark:from-indigo-500 dark:via-violet-500 dark:to-fuchsia-400 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant" className="text-indigo-100 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              POS Terminal
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-indigo-100 text-sm">
              {user?.first_name} {user?.last_name}
            </span>
            <div className="flex gap-2">
              <Link to="/merchant/pos/softpos">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Smartphone className="h-4 w-4 mr-1" />
                  SoftPOS
                </Button>
              </Link>
              <Link to="/merchant/pos/ai-assistant">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Sparkles className="h-4 w-4 mr-1" />
                  AI
                </Button>
              </Link>
              <Link to="/merchant/pos/rfid-analytics">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Radio className="h-4 w-4 mr-1" />
                  RFID
                </Button>
              </Link>
              <Link to="/merchant/pos/promotions">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Percent className="h-4 w-4 mr-1" />
                  Promos
                </Button>
              </Link>
              <Link to="/merchant/pos/staff">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Users className="h-4 w-4 mr-1" />
                  Staff
                </Button>
              </Link>
              <Link to="/merchant/pos/clock">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Clock className="h-4 w-4 mr-1" />
                  Clock
                </Button>
              </Link>
              <Link to="/merchant/pos/customers">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <UserCircle className="h-4 w-4 mr-1" />
                  Customers
                </Button>
              </Link>
              <Link to="/merchant/pos/reports">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Reports
                </Button>
              </Link>
              <Link to="/merchant/pos/feedback">
                <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Feedback
                </Button>
              </Link>
              <Link to="/merchant/services">
                <Button size="sm" className="bg-white text-indigo-700 hover:bg-indigo-50 border-0">
                  <CalendarDays className="h-4 w-4 mr-1" />
                  Services
                </Button>
              </Link>
              <Link to="/merchant/bookings">
                <Button size="sm" className="bg-white text-indigo-700 hover:bg-indigo-50 border-0">
                  <CalendarDays className="h-4 w-4 mr-1" />
                  Bookings
                </Button>
              </Link>
              <Link to="/merchant/pos/products">
                <Button size="sm" className="bg-white text-indigo-700 hover:bg-indigo-50 border-0">
                  <Package className="h-4 w-4 mr-1" />
                  Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Product Selection Area */}
        <div className="flex-1 flex flex-col border-r border-gray-200">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products or scan barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-200 focus:border-indigo-500 text-gray-900"
                data-testid="pos-search"
              />
            </div>
          </div>
          
          {/* Categories */}
          <div className="px-4 py-2 border-b border-gray-200 flex gap-2 overflow-x-auto">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
              className={selectedCategory === "all" ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700 hover:bg-gray-100"}
            >
              All
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.category_id}
                variant={selectedCategory === cat.category_id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.category_id)}
                className={selectedCategory === cat.category_id ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700 hover:bg-gray-100"}
              >
                {cat.name}
              </Button>
            ))}
          </div>
          
          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {products.map(product => (
                  <button
                    key={product.product_id}
                    onClick={() => addToCart(product)}
                    className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-100 shadow-md p-3 hover:border-indigo-400 hover:shadow-lg transition-all text-left group"
                    data-testid={`pos-product-${product.product_id}`}
                  >
                    <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center mb-2 overflow-hidden">
                      {product.image_url ? (
                        <img 
                          src={product.image_url.startsWith('data:') ? product.image_url : 
                               (product.image_url.startsWith('http') ? product.image_url : `${API}${product.image_url}`)} 
                          alt={product.name} 
                          className="w-full h-full object-cover" 
                        />
                      ) : product.images?.[0]?.url ? (
                        <img src={product.images[0].url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <p className="font-medium text-sm truncate group-hover:text-indigo-400">{product.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-semibold text-indigo-400">{formatPrice(product.retail_price)}</span>
                      <span className="text-xs text-gray-500">{product.total_stock} qty</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Cart Area */}
        <div className="w-96 flex flex-col bg-white border-l border-gray-200 shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Cart ({totals.itemsCount})
              </h2>
              {cart.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-gray-400 hover:text-red-500">
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {/* Customer Section */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            {selectedCustomer ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">{selectedCustomer.name}</p>
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {selectedCustomer.loyalty_points} pts
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={clearCustomer} className="text-gray-400 hover:text-red-500">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                  className="w-full border-gray-200 text-gray-700 justify-start hover:bg-gray-100"
                  data-testid="add-customer-btn"
                >
                  <UserCircle className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
                
                {showCustomerSearch && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 p-2 z-10 shadow-lg">
                    <Input
                      placeholder="Search name, email, phone..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="bg-white border-gray-200 text-gray-900 text-sm"
                      autoFocus
                      data-testid="customer-search-input"
                    />
                    {searchingCustomers && (
                      <div className="flex justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500"></div>
                      </div>
                    )}
                    {customerResults.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {customerResults.map(c => (
                          <button
                            key={c.customer_id}
                            onClick={() => selectCustomer(c)}
                            className="w-full p-2 text-left rounded hover:bg-gray-100 transition-colors"
                            data-testid={`customer-result-${c.customer_id}`}
                          >
                            <p className="font-medium text-sm text-gray-900">{c.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {c.email && <span>{c.email}</span>}
                              {c.phone && <span>{c.phone}</span>}
                              <span className="text-amber-600 flex items-center gap-1">
                                <Star className="h-3 w-3" />
                                {c.loyalty_points}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {customerSearch.length >= 2 && customerResults.length === 0 && !searchingCustomers && (
                      <p className="text-sm text-gray-500 text-center py-2">No customers found</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Loyalty Points Redemption */}
            {selectedCustomer && selectedCustomer.loyalty_points >= (loyaltyConfig?.min_redemption || 100) && cart.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 mb-2">Redeem loyalty points (100pts = £10)</p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={pointsToRedeem === 0 ? "outline" : "ghost"}
                    onClick={() => handleRedeemPoints(0)}
                    className={`text-xs ${pointsToRedeem === 0 ? "border-amber-500 text-amber-700 bg-amber-100" : "border-gray-200 text-gray-700"}`}
                  >
                    None
                  </Button>
                  {selectedCustomer.loyalty_points >= 100 && (
                    <Button
                      size="sm"
                      variant={pointsToRedeem === 100 ? "outline" : "ghost"}
                      onClick={() => handleRedeemPoints(100)}
                      className={`text-xs ${pointsToRedeem === 100 ? "border-amber-500 text-amber-700 bg-amber-100" : "border-gray-200 text-gray-700"}`}
                    >
                      100 (£10)
                    </Button>
                  )}
                  {selectedCustomer.loyalty_points >= 200 && (
                    <Button
                      size="sm"
                      variant={pointsToRedeem === 200 ? "outline" : "ghost"}
                      onClick={() => handleRedeemPoints(200)}
                      className={`text-xs ${pointsToRedeem === 200 ? "border-amber-500 text-amber-700 bg-amber-100" : "border-gray-200 text-gray-700"}`}
                    >
                      200 (£20)
                    </Button>
                  )}
                  {selectedCustomer.loyalty_points >= 500 && (
                    <Button
                      size="sm"
                      variant={pointsToRedeem === selectedCustomer.loyalty_points ? "outline" : "ghost"}
                      onClick={() => handleRedeemPoints(selectedCustomer.loyalty_points)}
                      className={`text-xs ${pointsToRedeem === selectedCustomer.loyalty_points ? "border-amber-500 text-amber-700 bg-amber-100" : "border-gray-200 text-gray-700"}`}
                    >
                      All ({selectedCustomer.loyalty_points})
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm">Cart is empty</p>
              </div>
            ) : (
              cart.map(item => (
                <div key={item.item_id} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid={`cart-item-${item.item_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.sku}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromCart(item.item_id)}
                      className="h-6 w-6 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => updateQuantity(item.item_id, -1)}
                        className="h-7 w-7 border-gray-200 text-gray-700"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center text-sm text-gray-900">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => updateQuantity(item.item_id, 1)}
                        className="h-7 w-7 border-gray-200 text-gray-700"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <span className="font-medium text-gray-900">{formatPrice(item.unit_price * item.quantity)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Totals & Pay */}
          <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatPrice(totals.subtotal)}</span>
              </div>
              {totals.discount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Discount</span>
                  <span>-{formatPrice(totals.discount)}</span>
                </div>
              )}
              {totals.loyaltyDiscount > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Loyalty Discount ({pointsToRedeem} pts)</span>
                  <span>-{formatPrice(totals.loyaltyDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>VAT (20%)</span>
                <span>{formatPrice(totals.tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 text-gray-900">
                <span>Total</span>
                <span>{formatPrice(totals.total)}</span>
              </div>
              {selectedCustomer && pointsToEarn > 0 && (
                <div className="flex justify-between text-amber-600 text-xs">
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    Points to earn
                  </span>
                  <span>+{pointsToEarn} pts</span>
                </div>
              )}
            </div>
            
            {/* Linked Booking Display */}
            {linkedBooking && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-700">Booking Linked</p>
                      <p className="text-xs text-gray-600">{linkedBooking.booking_number}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearLinkedBooking} className="text-gray-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDiscountModal(true)}
                disabled={cart.length === 0 || linkedBooking}
                className="border-gray-200 text-gray-700 hover:bg-gray-100"
              >
                <Tag className="h-4 w-4 mr-2" />
                Discount
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  fetchPendingBookings();
                  setShowBookingModal(true);
                }}
                disabled={linkedBooking || cart.length > 0}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="link-booking-btn"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Link Booking
              </Button>
            </div>
            
            <Button
              onClick={() => setShowPaymentModal(true)}
              disabled={cart.length === 0}
              className="w-full h-14 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:opacity-90 text-white text-lg"
              data-testid="pay-now-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Pay Now {formatPrice(totals.total)}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Payment</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-center text-3xl font-bold mb-6 text-gray-900">{formatPrice(totals.total)}</p>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => setPaymentMethod("cash")}
                className={`p-4 rounded-xl border-2 ${paymentMethod === "cash" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"} flex flex-col items-center gap-2 transition-all`}
                data-testid="payment-cash"
              >
                <Banknote className="h-6 w-6 text-gray-700" />
                <span className="text-sm text-gray-700">Cash</span>
              </button>
              <button
                onClick={() => setPaymentMethod("card")}
                className={`p-4 rounded-xl border-2 ${paymentMethod === "card" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"} flex flex-col items-center gap-2 transition-all`}
                data-testid="payment-card"
              >
                <CreditCard className="h-6 w-6 text-gray-700" />
                <span className="text-sm text-gray-700">Card</span>
              </button>
              <button
                onClick={() => setPaymentMethod("softpos")}
                className={`p-4 rounded-xl border-2 ${paymentMethod === "softpos" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"} flex flex-col items-center gap-2 transition-all`}
                data-testid="payment-softpos"
              >
                <Smartphone className="h-6 w-6 text-gray-700" />
                <span className="text-sm text-gray-700">SoftPOS</span>
              </button>
            </div>
            
            {paymentMethod === "cash" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Amount Tendered</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cashTendered}
                    onChange={(e) => setCashTendered(e.target.value)}
                    placeholder="0.00"
                    className="bg-gray-50 border-gray-200 text-xl h-14 text-center"
                    data-testid="cash-amount-input"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[10, 20, 50, 100].map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      onClick={() => setCashTendered(amount.toString())}
                      className="border-gray-200 text-gray-700 hover:bg-gray-100"
                    >
                      £{amount}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => setCashTendered((totals.total / 100).toFixed(2))}
                    className="border-gray-200 text-gray-700 hover:bg-gray-100"
                  >
                    Exact
                  </Button>
                </div>
                {cashTendered && parseFloat(cashTendered) * 100 >= totals.total && (
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-gray-600 text-sm">Change Due</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatPrice(changeDue)}</p>
                  </div>
                )}
              </div>
            )}
            
            {paymentMethod === "card" && (
              <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-indigo-500" />
                <p className="text-gray-900 font-medium">Secure Card Payment</p>
                <p className="text-gray-500 text-sm mt-2">Powered by Stripe</p>
                <p className="text-xs text-gray-400 mt-4">You will be redirected to Stripe's secure checkout</p>
              </div>
            )}
            
            {paymentMethod === "softpos" && (
              <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                <Smartphone className="h-12 w-12 mx-auto mb-4 text-indigo-500" />
                <p className="text-gray-600">Customer taps card on your phone</p>
                <p className="text-xs text-gray-400 mt-2">(SoftPOS coming soon)</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="border-gray-200 text-gray-700">
              Cancel
            </Button>
            <Button
              onClick={processPayment}
              disabled={processingPayment || (paymentMethod === "cash" && (!cashTendered || parseFloat(cashTendered) * 100 < totals.total))}
              className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white hover:opacity-90"
              data-testid="confirm-payment-btn"
            >
              {processingPayment ? "Processing..." : "Complete Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              <Check className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
              Payment Successful!
            </DialogTitle>
          </DialogHeader>
          
          {completedTransaction && (
            <div className="py-4 text-center">
              <p className="text-3xl font-bold mb-2 text-gray-900">{formatPrice(completedTransaction.totals?.total || 0)}</p>
              <p className="text-gray-500">Receipt: {completedTransaction.receipt_number}</p>
              {completedTransaction.change_due > 0 && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-gray-600">Change Due</p>
                  <p className="text-xl font-bold text-emerald-600">{formatPrice(completedTransaction.change_due)}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              className="border-gray-200 text-gray-700 flex-1"
              onClick={() => {
                if (completedTransaction?.transaction_id) {
                  navigate(`/merchant/pos/receipt/${completedTransaction.transaction_id}`);
                }
              }}
            >
              <Receipt className="h-4 w-4 mr-2" />
              Print Receipt
            </Button>
            <Button
              onClick={() => setShowReceiptModal(false)}
              className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white flex-1"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex gap-2">
              <Button
                variant={discountType === "percentage" ? "default" : "outline"}
                onClick={() => setDiscountType("percentage")}
                className={discountType === "percentage" ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700"}
              >
                Percentage
              </Button>
              <Button
                variant={discountType === "fixed" ? "default" : "outline"}
                onClick={() => setDiscountType("fixed")}
                className={discountType === "fixed" ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700"}
              >
                Fixed Amount
              </Button>
            </div>
            
            <div>
              <label className="text-sm text-gray-600">
                {discountType === "percentage" ? "Discount %" : "Discount Amount (£)"}
              </label>
              <Input
                type="number"
                min="0"
                step={discountType === "percentage" ? "1" : "0.01"}
                max={discountType === "percentage" ? "100" : undefined}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === "percentage" ? "10" : "5.00"}
                className="bg-gray-50 border-gray-200"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600">Promo Code (optional)</label>
              <Input
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="SAVE10"
                className="bg-gray-50 border-gray-200"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)} className="border-gray-200 text-gray-700">
              Cancel
            </Button>
            <Button onClick={applyDiscount} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Booking Selection Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-emerald-600" />
              Link to Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">Select a booking to complete payment for the remaining balance.</p>
            
            {loadingBookings ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : pendingBookings.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No pending bookings to complete</p>
                <p className="text-xs text-gray-400 mt-2">Bookings appear here after deposit is paid</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingBookings.map((booking) => (
                  <button
                    key={booking.booking_id}
                    onClick={() => linkBooking(booking)}
                    className="w-full p-4 rounded-xl bg-gray-50 border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left"
                    data-testid={`booking-select-${booking.booking_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-emerald-600">{booking.booking_number}</span>
                      <span className="text-sm text-gray-500">{booking.status}</span>
                    </div>
                    <p className="font-semibold mb-2 text-gray-900">{booking.service_name}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {booking.booking_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {booking.start_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {booking.staff_name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <div className="text-xs text-gray-500">
                        <span>Customer: {booking.customer_name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Remaining Balance</p>
                        <p className="font-bold text-emerald-600">{formatPrice(booking.final_amount)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)} className="border-gray-200 text-gray-700">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Product Customization Modal (Variants & Modifiers) */}
      <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              Customize {customizingProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {customizingProduct && (
            <div className="py-4 space-y-6">
              {/* Variants Section */}
              {customizingProduct.has_variants && customizingProduct.variants?.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {customizingProduct.variant_label || "Option"} *
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {customizingProduct.variants
                      .filter(v => v.is_active !== false)
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                      .map((variant) => (
                        <button
                          key={variant.variant_id}
                          onClick={() => setSelectedVariant(variant)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            selectedVariant?.variant_id === variant.variant_id
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`variant-${variant.variant_id}`}
                        >
                          <p className="font-medium text-sm">{variant.name}</p>
                          <p className="text-indigo-600 font-semibold text-sm mt-1">
                            {formatPrice(variant.price || variant.retail_price)}
                          </p>
                        </button>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Modifiers Section */}
              {customizingProduct.has_modifiers && customizingProduct.modifiers?.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {customizingProduct.modifier_label || "Add-ons"}
                  </Label>
                  <div className="space-y-2">
                    {customizingProduct.modifiers
                      .filter(m => m.is_active !== false)
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                      .map((modifier) => {
                        const qty = selectedModifiers[modifier.modifier_id] || 0;
                        return (
                          <div
                            key={modifier.modifier_id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              qty > 0 ? "border-emerald-300 bg-emerald-50" : "border-gray-200"
                            }`}
                            data-testid={`modifier-${modifier.modifier_id}`}
                          >
                            <div>
                              <p className="font-medium text-sm">{modifier.name}</p>
                              <p className="text-emerald-600 text-sm">+{formatPrice(modifier.price)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateModifierQuantity(modifier.modifier_id, -1)}
                                disabled={qty === 0}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="w-6 text-center font-medium">{qty}</span>
                              <button
                                onClick={() => updateModifierQuantity(modifier.modifier_id, 1)}
                                disabled={qty >= (modifier.max_quantity || 99)}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              
              {/* Price Summary */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Price</span>
                  <span className="text-xl font-bold text-indigo-600">
                    {formatPrice(
                      (selectedVariant?.price || selectedVariant?.retail_price || customizingProduct.retail_price) +
                      Object.entries(selectedModifiers).reduce((sum, [modId, qty]) => {
                        const mod = customizingProduct.modifiers?.find(m => m.modifier_id === modId);
                        return sum + ((mod?.price || 0) * qty);
                      }, 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCustomizeModal(false);
                setCustomizingProduct(null);
              }} 
              className="border-gray-200 text-gray-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmCustomization} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-customization-btn"
            >
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSTerminalPage;
